import { FrameProcessor, Message } from '@ricky0123/vad-web';
import { SileroV5 } from '@ricky0123/vad-web/dist/models/v5';
import * as ort from 'onnxruntime-web';

// Configure ONNX Runtime WASM before anything loads
ort.env.wasm.numThreads = 1;
ort.env.wasm.simd = true;
ort.env.wasm.wasmPaths = '/';

const V5_FRAME_SAMPLES = 512;
const TARGET_SAMPLE_RATE = 16000;
const MS_PER_FRAME = (V5_FRAME_SAMPLES / TARGET_SAMPLE_RATE) * 1000;

interface SileroModel {
  process: (frame: Float32Array) => Promise<{ isSpeech: number; notSpeech: number }>;
  reset_state: () => void;
}

let modelInstance: SileroModel | null = null;
let modelLoading: Promise<SileroModel> | null = null;
let processingLock: Promise<void> = Promise.resolve();

async function getModel(): Promise<SileroModel> {
  if (modelInstance) return modelInstance;
  if (modelLoading) return modelLoading;

  modelLoading = (async () => {
    const res = await fetch('/silero_vad_v5.onnx');
    const buf = await res.arrayBuffer();
    const model = await SileroV5.new(ort, () => Promise.resolve(buf));
    return model as SileroModel;
  })();

  try {
    modelInstance = await modelLoading;
    return modelInstance;
  } catch (err) {
    modelLoading = null;
    throw err;
  }
}

export interface VadSegment {
  start: number; // ms
  end: number; // ms
  audio: Float32Array;
}

export interface VadResult {
  hasSpeech: boolean;
  segments: VadSegment[];
  totalDurationSec: number;
}

function resampleAndFrame(audio: Float32Array, sampleRate: number): Float32Array[] {
  const ratio = sampleRate / TARGET_SAMPLE_RATE;
  const outputLen = Math.floor(audio.length / ratio);
  const resampled = new Float32Array(outputLen);
  for (let i = 0; i < outputLen; i++) {
    resampled[i] = audio[Math.round(i * ratio)];
  }

  const frames: Float32Array[] = [];
  for (let i = 0; i + V5_FRAME_SAMPLES <= resampled.length; i += V5_FRAME_SAMPLES) {
    frames.push(resampled.slice(i, i + V5_FRAME_SAMPLES));
  }
  return frames;
}

/**
 * Run Silero VAD V5 on audio to detect speech segments.
 * Serialized — ONNX session cannot handle concurrent runs.
 */
export async function vadCheckSpeech(
  audio: Float32Array,
  sampleRate: number,
): Promise<VadResult> {
  const totalDurationSec = audio.length / sampleRate;

  // Serialize access — ONNX session cannot handle concurrent runs
  let unlock: () => void;
  const waitForLock = processingLock;
  processingLock = new Promise<void>((resolve) => { unlock = resolve; });
  await waitForLock;

  try {
    const model = await getModel();
    model.reset_state();

    const frameProcessor = new FrameProcessor(
      model.process,
      model.reset_state,
      {
        positiveSpeechThreshold: 0.5,
        negativeSpeechThreshold: 0.35,
        redemptionMs: 300,
        preSpeechPadMs: 250,
        minSpeechMs: 50,
        submitUserSpeechOnPause: false,
      },
      MS_PER_FRAME,
    );
    frameProcessor.resume();

    const frames = resampleAndFrame(audio, sampleRate);
    const segments: VadSegment[] = [];
    let speechStart = 0;

    for (let i = 0; i < frames.length; i++) {
      const events: Array<{ msg: Message; audio?: Float32Array }> = [];
      await frameProcessor.process(frames[i], (event) => {
        events.push(event as { msg: Message; audio?: Float32Array });
      });

      for (const event of events) {
        if (event.msg === Message.SpeechStart) {
          speechStart = (i * V5_FRAME_SAMPLES) / TARGET_SAMPLE_RATE * 1000;
        } else if (event.msg === Message.SpeechEnd && event.audio) {
          const end = ((i + 1) * V5_FRAME_SAMPLES) / TARGET_SAMPLE_RATE * 1000;
          segments.push({ start: speechStart, end, audio: event.audio });
        }
      }
    }

    // Flush any remaining speech
    frameProcessor.endSegment((event) => {
      const ev = event as { msg?: Message; audio?: Float32Array };
      if (ev.msg === Message.SpeechEnd && ev.audio) {
        const end = (frames.length * V5_FRAME_SAMPLES) / TARGET_SAMPLE_RATE * 1000;
        segments.push({ start: speechStart, end, audio: ev.audio });
      }
    });

    return { hasSpeech: segments.length > 0, segments, totalDurationSec };
  } catch (err) {
    console.error('[VAD] Error — assuming speech present:', err);
    return { hasSpeech: true, segments: [], totalDurationSec };
  } finally {
    unlock!();
  }
}

/**
 * Trim audio to only speech portions using VAD segments.
 * Concatenates speech segment audio from the FrameProcessor.
 */
export function trimAudio(segments: VadSegment[]): Float32Array {
  if (segments.length === 0) return new Float32Array(0);

  const totalLength = segments.reduce((sum, s) => sum + s.audio.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const seg of segments) {
    result.set(seg.audio, offset);
    offset += seg.audio.length;
  }
  return result;
}
